import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

function UpdateHike() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hikes, setHikes] = useState([]);
  const [selectedHikeId, setSelectedHikeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    kmTravelled: '',
    rpe: '5',
    moneySpent: '',
    mood: '5',
    sleepQuality: '5',
    overallFeeling: '5',
    caloriesSpent: '',
    weatherTemp: 'Mild',
    weatherType: 'Sunny'
  });

  const weatherTempOptions = [
    { value: 'Hot', label: 'Hot 🔥', icon: 'fa-temperature-high' },
    { value: 'Mild', label: 'Mild 🌤️', icon: 'fa-temperature-half' },
    { value: 'Cold', label: 'Cold 🥶', icon: 'fa-temperature-low' }
  ];

  const weatherTypeOptions = [
    { value: 'Sunny', label: 'Sunny ☀️', icon: 'fa-sun' },
    { value: 'Cloudy', label: 'Cloudy ☁️', icon: 'fa-cloud' },
    { value: 'Rain', label: 'Rain 🌧️', icon: 'fa-cloud-rain' },
    { value: 'Storm', label: 'Storm ⛈️', icon: 'fa-bolt' },
    { value: 'Windy', label: 'Windy 🌬️', icon: 'fa-wind' },
    { value: 'Snow', label: 'Snow 🌨️', icon: 'fa-snowflake' }
  ];

  useEffect(() => {
    fetchHikes();
  }, []);

  const fetchHikes = async () => {
    try {
      const response = await fetch('/api/hikes', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch hikes');
      }

      const data = await response.json();
      setHikes(data);
      
      // Auto-select first hike if available
      if (data.length > 0 && !selectedHikeId) {
        setSelectedHikeId(data[0]._id);
      }
    } catch (err) {
      setError('Failed to load hikes. Please try again.');
      console.error('Error fetching hikes:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleHikeChange = (e) => {
    setSelectedHikeId(e.target.value);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!selectedHikeId) {
      setError('Please select a hike');
      setLoading(false);
      return;
    }

    // Validation
    if (!formData.kmTravelled || !formData.moneySpent || !formData.caloriesSpent) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `/api/hikes/${selectedHikeId}/entries`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            date: formData.date,
            kmTravelled: parseFloat(formData.kmTravelled),
            rpe: parseInt(formData.rpe),
            moneySpent: parseFloat(formData.moneySpent),
            mood: parseInt(formData.mood),
            sleepQuality: parseInt(formData.sleepQuality),
            overallFeeling: parseInt(formData.overallFeeling),
            caloriesSpent: parseFloat(formData.caloriesSpent),
            weatherTemp: formData.weatherTemp,
            weatherType: formData.weatherType
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create entry');
      }

      setSuccess('Entry added successfully!');
      
      // Reset form but keep the selected hike and date
      setFormData({
        date: formData.date,
        kmTravelled: '',
        rpe: '5',
        moneySpent: '',
        mood: '5',
        sleepQuality: '5',
        overallFeeling: '5',
        caloriesSpent: '',
        weatherTemp: 'Mild',
        weatherType: 'Sunny'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Please log in to update your hike.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Update Hike</h1>
        <div className="space-x-2">
          <button
            onClick={() => navigate('/hike/setup')}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            + New Hike
          </button>
          <button
            onClick={() => navigate('/hike/report')}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            View Reports
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {hikes.length === 0 ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">No hikes found</p>
          <p className="text-sm">Create your first hike to start tracking your journey!</p>
          <button
            onClick={() => navigate('/hike/setup')}
            className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Setup Your First Hike
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hike Selection */}
            <div className="mb-4 md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hike">
                Hike *
              </label>
              <select
                id="hike"
                value={selectedHikeId}
                onChange={handleHikeChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="">Select a hike</option>
                {hikes.map(hike => (
                  <option key={hike._id} value={hike._id}>
                    {hike.name} ({hike.totalDistance} km, ${hike.budget})
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            {/* KM Travelled */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="kmTravelled">
                KM Travelled *
              </label>
              <input
                type="number"
                id="kmTravelled"
                name="kmTravelled"
                value={formData.kmTravelled}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="e.g., 25.5"
                step="0.1"
                min="0"
                required
              />
            </div>

            {/* RPE */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="rpe">
                RPE (/10) *
              </label>
              <select
                id="rpe"
                name="rpe"
                value={formData.rpe}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Money Spent */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="moneySpent">
                $ Spent *
              </label>
              <input
                type="number"
                id="moneySpent"
                name="moneySpent"
                value={formData.moneySpent}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="e.g., 75.50"
                step="0.01"
                min="0"
                required
              />
            </div>

            {/* Mood */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mood">
                Mood (/10) *
              </label>
              <select
                id="mood"
                name="mood"
                value={formData.mood}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Sleep Quality */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="sleepQuality">
                Sleep Quality (/10) *
              </label>
              <select
                id="sleepQuality"
                name="sleepQuality"
                value={formData.sleepQuality}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Overall Feeling */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="overallFeeling">
                Overall Feeling (/10) *
              </label>
              <select
                id="overallFeeling"
                name="overallFeeling"
                value={formData.overallFeeling}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Calories Spent */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="caloriesSpent">
                Calories Spent *
              </label>
              <input
                type="number"
                id="caloriesSpent"
                name="caloriesSpent"
                value={formData.caloriesSpent}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="e.g., 3500"
                step="1"
                min="0"
                required
              />
            </div>

            {/* Weather Temp */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weatherTemp">
                Weather Temperature *
              </label>
              <select
                id="weatherTemp"
                name="weatherTemp"
                value={formData.weatherTemp}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {weatherTempOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Weather Type */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weatherType">
                Weather Type *
              </label>
              <select
                id="weatherType"
                name="weatherType"
                value={formData.weatherType}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {weatherTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : 'Add Entry'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default UpdateHike;
